import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ImageSquare, UserPlus } from "@phosphor-icons/react";

const LABEL = "text-xs uppercase tracking-wider font-semibold text-muted-foreground";

const Preview = ({ f, img }) => (
  <div className="rounded-lg bg-[#e5ddd5] p-3 min-h-[200px]" data-testid="template-preview">
    <div className="bg-white rounded-lg shadow-sm max-w-[280px] overflow-hidden text-[13px] text-zinc-900">
      {f.header_type === "image" && (img ? <img src={img} alt="" className="w-full h-36 object-cover" /> :
        <div className="h-28 bg-zinc-200 flex items-center justify-center text-zinc-500"><ImageSquare size={28} /></div>)}
      <div className="p-2.5">
        {f.header_type === "text" && f.header_text && <div className="font-bold mb-1">{f.header_text}</div>}
        <div className="whitespace-pre-wrap">{(f.body || "Escribe el mensaje…").replaceAll("{{1}}", "Ana")}</div>
        {f.footer && <div className="text-[11px] text-zinc-500 mt-1.5">{f.footer}</div>}
      </div>
    </div>
    <div className="text-[10px] text-zinc-600 mt-2">Vista previa con «Ana» como nombre del cliente.</div>
  </div>
);

export function TemplateForm({ accountId, onCancel, onCreated }) {
  const [f, setF] = useState({ name: "", category: "MARKETING", language: "es", header_type: "none", header_text: "", body: "", footer: "Santo Domingo Motors" });
  const [img, setImg] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) return toast.error("La imagen debe ser JPG o PNG");
    if (file.size > 5 * 1024 * 1024) return toast.error("Máximo 5 MB");
    setImg(URL.createObjectURL(file)); setFileId(null);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "image");
    try { const { data } = await api.post("/files", fd); setFileId(data.id); }
    catch { toast.error("No se pudo subir la imagen"); setImg(null); }
  };

  const submit = async () => {
    if (!f.name.trim() || !f.body.trim()) return toast.error("Nombre y mensaje son obligatorios");
    if (f.header_type === "image" && !fileId) return toast.error("Sube la imagen del encabezado");
    setBusy(true);
    try {
      const { data } = await api.post("/wa-templates", { ...f, header_file_id: fileId }, { params: accountId ? { channel_account_id: accountId } : {} });
      toast.success(`Plantilla «${data.name}» enviada a Meta para aprobación`);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Meta rechazó la plantilla"); } finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-lg p-4 grid md:grid-cols-[1fr_300px] gap-4" data-testid="template-form">
      <div className="space-y-3">
        <div>
          <label className={LABEL}>Nombre (minúsculas y guion bajo)</label>
          <Input value={f.name} onChange={(e) => set("name")(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} placeholder="promo_suzuki_junio" className="mt-1" data-testid="template-form-name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={LABEL}>Categoría</label>
            <Select value={f.category} onValueChange={set("category")}>
              <SelectTrigger className="mt-1" data-testid="template-form-category"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MARKETING">Marketing</SelectItem><SelectItem value="UTILITY">Utilidad</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <label className={LABEL}>Idioma</label>
            <Select value={f.language} onValueChange={set("language")}>
              <SelectTrigger className="mt-1" data-testid="template-form-language"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="es">Español</SelectItem><SelectItem value="en_US">Inglés (EE. UU.)</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className={LABEL}>Encabezado</label>
          <Select value={f.header_type} onValueChange={set("header_type")}>
            <SelectTrigger className="mt-1" data-testid="template-form-header-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Sin encabezado</SelectItem><SelectItem value="text">Texto</SelectItem><SelectItem value="image">Imagen</SelectItem></SelectContent>
          </Select>
          {f.header_type === "text" && <Input value={f.header_text} onChange={set("header_text")} maxLength={60} placeholder="¡Ofertas de junio!" className="mt-2" data-testid="template-form-header-text" />}
          {f.header_type === "image" && (
            <div className="mt-2 text-xs">
              <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border cursor-pointer hover:bg-secondary text-xs font-medium">
                <ImageSquare size={14} /> {img ? "Cambiar imagen" : "Subir imagen"}
                <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="template-form-header-image" />
              </label>
              <div className="text-muted-foreground mt-1">{img && !fileId ? "Subiendo…" : "JPG o PNG, máx. 5 MB. Esta imagen se envía en cada mensaje de la campaña."}</div>
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className={LABEL}>Mensaje</label>
            <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px] gap-1" onClick={() => set("body")(`${f.body}{{1}}`)} data-testid="template-form-insert-name">
              <UserPlus size={12} /> Insertar nombre del cliente
            </Button>
          </div>
          <Textarea value={f.body} onChange={set("body")} rows={5} maxLength={1024} className="mt-1" data-testid="template-form-body"
            placeholder="Hola {{1}}, este mes tenemos ofertas especiales en la línea Suzuki…" />
          <div className="text-[10px] text-muted-foreground mt-1">{"{{1}}"} se reemplaza por el nombre del cliente. No empieces ni termines el mensaje con {"{{1}}"}.</div>
        </div>
        <div>
          <label className={LABEL}>Pie de mensaje (opcional)</label>
          <Input value={f.footer} onChange={set("footer")} maxLength={60} className="mt-1" data-testid="template-form-footer" />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} data-testid="template-form-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy || (f.header_type === "image" && img && !fileId)} data-testid="template-form-submit">{busy ? "Enviando a Meta…" : "Enviar a Meta para aprobación"}</Button>
        </div>
      </div>
      <Preview f={f} img={img} />
    </div>
  );
}

export default TemplateForm;
